import { GridRenderCellParams } from '@mui/x-data-grid/models/params/gridCellParams';
import { IconButton, Tooltip } from 'react-scale/components/design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { copyToClipboard } from 'react-scale/lib/clipboard';
import _ from 'lodash';
import moment from 'moment';
import { isActiveOperation } from '@scale/shared/qaOperation/stateMachineDefs/qaOperationTransitionGuards';
import { QAOperationJson, QAOperationType } from '@scale/shared/types/qaOperation/qaOperation';
import React from 'react';
import SAlert from 'react-s-alert';
import { remotasksURL } from 'react-scale/lib/urls';
import { parseDuration } from '../createQAOperation/customInputs/helpers';
import { AuditViewType } from '@scale/shared/types/corp/attemptAudit';

export const idCopyCell = ({ value }: GridRenderCellParams<string>) => {
  return (
    value && (
      <>
        {value.slice(-5)}
        <Tooltip placement="top" title="Copy to clipboard">
          <IconButton
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              copyToClipboard(value, { excludeOuterQuotes: true });
            }}
          >
            <FontAwesomeIcon icon="copy" color="gray" />
          </IconButton>
        </Tooltip>
      </>
    )
  );
};

export const cellWithToolTip = ({ value }: GridRenderCellParams<string>) => {
  return (
    <Tooltip placement="top-start" title={value}>
      <div>{_.startCase(value)}</div>
    </Tooltip>
  );
};

export const cellWithToolTipNoStartCase = ({ value }: GridRenderCellParams<string>) => {
  return (
    <Tooltip placement="top-start" title={value}>
      <div>{value}</div>
    </Tooltip>
  );
};

export const isOverdue = (operation: QAOperationJson): boolean => {
  const now = moment().utc().startOf('day');
  const dueDate = (operation.dueDate && moment(new Date(operation.dueDate)).utc()) || undefined;
  if (!dueDate) return false;
  return isActiveOperation(operation) && dueDate.diff(now) < 0;
};

export const renderDueDate = ({ value }: GridRenderCellParams) => {
  if (!value || !value.operation || !value.operation.dueDate) return <div></div>;
  const operation = value.operation as QAOperationJson;
  const dueDate = operation.dueDate && moment(new Date(operation.dueDate)).utc();
  const style = isOverdue(value.operation) ? { color: '#6f0015', fontWeight: 'bold' } : undefined;
  return (
    <Tooltip placement="top-start" title={dueDate.format('MM/DD/YYYY')}>
      <div style={style}> {dueDate.format('MM/DD/YYYY')} </div>
    </Tooltip>
  );
};

function pad(str: string | number) {
  return ('0' + str).slice(-2);
}
function formatTime(seconds: number): string {
  const { hh, mm, ss } = parseDuration(seconds);
  return `${hh ? pad(hh) + ':' : ''}${pad(mm)}:${pad(ss)}`;
}

export const renderAttemptTimeSpent = ({ value }: GridRenderCellParams) => {
  if (!value || !value.operation) return <div> - </div>;
  const operation = value.operation as QAOperationJson;
  const timeSpent = operation.attemptTimeSpent ?? 0;
  const maxTime = operation.maxTimeRequired ?? 0;
  const style =
    timeSpent > maxTime && maxTime > 0 ? { color: '#6f0015', fontWeight: 'bold' } : undefined;
  return (
    <time
      dateTime={`P${Math.round(timeSpent)}S`}
      style={{ ...style, width: '100%', textAlign: 'center' }}
    >
      {formatTime(timeSpent)} / {maxTime ? `${formatTime(maxTime)}` : 'Not Set'}
    </time>
  );
};

export function validateQAOperationParams(qaOperation?: QAOperationJson) {
  try {
    const params = new URLSearchParams(window.location.search);
    const qaOperationId = params.get('qaOperationId');
    if (qaOperation && qaOperation._id && !qaOperationId) {
      params.set('qaOperationId', qaOperation._id);
      window.location.search = params.toString();
      SAlert.info(`Reloading with timer for QA Operation ${qaOperation._id}`);
    }
  } catch {
    // do nothing
  }
}

interface IOperationLinkProps {
  href: string;
  forceRemoAdminLink?: boolean;
  queryParams?: Record<string, any>;
}

const formatOperationQueryParams = (
  operation: QAOperationJson,
  additionalParams: Record<string, any> = {},
) => {
  return { closeOnComplete: 1, qaOperationId: operation._id, ...additionalParams };
};

const getBasePathName = () =>
  window.location.href.includes('/expert') ? 'expert/outlieradmin' : 'remoadmin';

const operationHRefMap: Record<
  QAOperationType,
  (operation: QAOperationJson, isRA: boolean) => IOperationLinkProps | undefined
> = {
  [QAOperationType.SpeedAudit]: (operation: QAOperationJson, isRemoAdmin: boolean) => {
    const basePathname = getBasePathName();
    const id = operation.relatedObjectId || operation.params.speedAuditId;
    let href;
    if (operation.params?.auditBatchView === AuditViewType.CHAT_BULK_AUDIT) {
      href = isRemoAdmin
        ? `/${basePathname}/tools/chat_bulk_audit/${id}`
        : `/corp/chat_bulk_audit/${id}`;
    } else {
      href = `/${
        isRemoAdmin ? `${basePathname}/tools/speed_auditv2` : 'corp/v2/speed_audit'
      }/${id}`;
    }
    return { href, queryParams: formatOperationQueryParams(operation) };
  },
  [QAOperationType.CreateScenario]: (operation: QAOperationJson, isRemoAdmin: boolean) => {
    const basePathname = getBasePathName();
    const id = operation.relatedObjectId || operation.params.subtaskId;
    const href = isRemoAdmin
      ? `/${basePathname}/tools/create_benchmark/${id}`
      : `/corp/create_scenario`;
    return { href, queryParams: formatOperationQueryParams(operation, { subtaskId: id }) };
  },
  [QAOperationType.EditScenario]: (operation: QAOperationJson, isRemoAdmin: boolean) => {
    const basePathname = getBasePathName();
    const id = operation.relatedObjectId || operation.params.scenarioId;
    const href = isRemoAdmin
      ? `/${basePathname}/tools/edit_benchmark/${id}`
      : `/corp/edit_scenario`;
    return { href, queryParams: formatOperationQueryParams(operation, { scenarioId: id }) };
  },
  [QAOperationType.SendToCustomer]: (operation: QAOperationJson, isRemoAdmin: boolean) => {
    const basePathname = getBasePathName();
    const projectId = operation.project || operation.params.projectId;
    const batchId = operation.relatedObjectId || operation.params.actionBatchId;
    const href = isRemoAdmin
      ? `/${basePathname}/tools/action/send_tasks_to_customer/${projectId}`
      : `/corp/qm/queue/${projectId}/throughput`;
    return { href, queryParams: formatOperationQueryParams(operation, { batchId }) };
  },
  [QAOperationType.InTaskSupport]: (operation: QAOperationJson, isRemoAdmin: boolean) => {
    const basePathname = getBasePathName();
    const href = isRemoAdmin
      ? `/${basePathname}/tools/intask_support_ticket_details/${operation.relatedObjectId}`
      : `/corp/support_ticket_detail/${operation.relatedObjectId}`;
    return { href, queryParams: formatOperationQueryParams(operation) };
  },
  [QAOperationType.AuditTasker]: (operation: QAOperationJson, _isRemoAdmin: boolean) => {
    const href = `/bpo/${operation.relatedObjectId}/audit`;
    return { href, queryParams: formatOperationQueryParams(operation), forceRemoAdminLink: true };
  },
  [QAOperationType.Instructions]: (operation: QAOperationJson, _isRA: boolean) => {
    const basePathname = getBasePathName();
    const href = `/${basePathname}/operations/instructions`;
    return { href, queryParams: formatOperationQueryParams(operation), forceRemoAdminLink: true };
  },
};

const getOperationURL = (operation: QAOperationJson, isRemoAdmin: boolean) => {
  const operationLinkProps = operationHRefMap[operation.type](operation, isRemoAdmin);
  if (!operationLinkProps) return;

  const { href, queryParams, forceRemoAdminLink } = operationLinkProps;
  const url = new URL(window.location.href);
  if (forceRemoAdminLink) {
    url.href = remotasksURL(href);
    url.search = window.location.search;
  } else {
    url.pathname = href;
  }
  if (queryParams) {
    _.forEach(queryParams, (value, key) => url.searchParams.set(key, value));
  }
  return url;
};

export const openOperationPopup = (
  operation: QAOperationJson,
  isRemoAdmin: boolean,
  onClose?: () => void,
) => {
  const url = getOperationURL(operation, isRemoAdmin);
  if (!url) return;
  const w = window.open(url, '_blank', 'toolbar=0, location=0, menubar=0, addressbar=0');
  if (w && onClose) {
    const timer = setInterval(() => {
      if (w.closed) {
        clearInterval(timer);
        onClose();
      }
    }, 1000);
  }
};
